package dev.youndie.site.pages

import androidx.compose.runtime.*
import com.varabyte.kobweb.core.Page
import com.varabyte.kobweb.core.data.*
import com.varabyte.kobweb.core.layout.Layout
import com.varabyte.kobweb.core.layout.NoLayout
import com.varabyte.kobweb.core.init.InitRoute
import com.varabyte.kobweb.core.init.InitRouteContext
import com.varabyte.kobwebx.frontmatter.*
import com.varabyte.kobwebx.markdown.*

@InitRoute
fun initMostikPage(ctx: InitRouteContext) {
    ctx.data.add(MarkdownContext("mostik.md", FrontMatterElement.Builder { addScalar("title", "mostik"); addScalar("searchTitle", "HTTP to Kafka bridge in Kotlin/Native — the status code is a true statement about the record"); addScalar("description", "An HTTP → Kafka bridge whose status code is a true statement about the record: 200 means the broker acknowledged it, 429 means it was never queued, 504 means nobody knows. Kotlin/Native and JVM from one source, the JVM build the oracle for the native one."); addScalar("library", "mostik"); }))
}

@Page("/mostik")
@Layout(".components.layouts.LibraryLayout")
@Composable
fun MostikPage() {
    org.jetbrains.compose.web.dom.H2(attrs = { id("why-it-exists") }) {
        org.jetbrains.compose.web.dom.Text("Why it exists")
    }
    org.jetbrains.compose.web.dom.P {
        org.jetbrains.compose.web.dom.Text("The usual HTTP-to-Kafka bridge answers ")
        org.jetbrains.compose.web.dom.Code {
            org.jetbrains.compose.web.dom.Text("200")
        }
        org.jetbrains.compose.web.dom.Text(" the moment the record is queued in its local client. After that it either waits for the broker without a bound, or cuts the wait and answers ")
        org.jetbrains.compose.web.dom.Code {
            org.jetbrains.compose.web.dom.Text("503")
        }
        org.jetbrains.compose.web.dom.Text(" for a record that lands anyway — and the caller, reading a failure, sends it again. ")
        com.varabyte.kobweb.silk.components.navigation.Link("https://github.com/youndie/mostik") {
            org.jetbrains.compose.web.dom.Text("mostik")
        }
        org.jetbrains.compose.web.dom.Text(" waits for the broker's acknowledgement under a deadline, and when it cannot know what happened it says so: ")
        org.jetbrains.compose.web.dom.Code {
            org.jetbrains.compose.web.dom.Text("504")
        }
        org.jetbrains.compose.web.dom.Text(", with ")
        org.jetbrains.compose.web.dom.Code {
            org.jetbrains.compose.web.dom.Text("\"outcome\": \"unknown\"")
        }
        org.jetbrains.compose.web.dom.Text(" and ")
        org.jetbrains.compose.web.dom.Code {
            org.jetbrains.compose.web.dom.Text("\"retrySafe\": false")
        }
        org.jetbrains.compose.web.dom.Text(" in the body. The set of answers is closed; no route here answers ")
        org.jetbrains.compose.web.dom.Code {
            org.jetbrains.compose.web.dom.Text("500")
        }
        org.jetbrains.compose.web.dom.Text(".")
    }
    org.jetbrains.compose.web.dom.P {
        org.jetbrains.compose.web.dom.Em {
            org.jetbrains.compose.web.dom.Text("mostik")
        }
        org.jetbrains.compose.web.dom.Text(" is Russian for \"a small bridge\".")
    }
    org.jetbrains.compose.web.dom.H2(attrs = { id("what-you-get") }) {
        org.jetbrains.compose.web.dom.Text("What you get")
    }
    org.jetbrains.compose.web.dom.P {
        org.jetbrains.compose.web.dom.Text("One route, ")
        org.jetbrains.compose.web.dom.Code {
            org.jetbrains.compose.web.dom.Text("POST /topics/{topic}/records")
        }
        org.jetbrains.compose.web.dom.Text(". The body is the record's value, byte for byte, never parsed; ")
        org.jetbrains.compose.web.dom.Code {
            org.jetbrains.compose.web.dom.Text("Record-Key")
        }
        org.jetbrains.compose.web.dom.Text(" is the key; each ")
        org.jetbrains.compose.web.dom.Code {
            org.jetbrains.compose.web.dom.Text("Record-Header-<name>")
        }
        org.jetbrains.compose.web.dom.Text(" becomes a header named ")
        org.jetbrains.compose.web.dom.Code {
            org.jetbrains.compose.web.dom.Text("<name>")
        }
        org.jetbrains.compose.web.dom.Text(".")
    }
    org.jetbrains.compose.web.dom.Table {
        org.jetbrains.compose.web.dom.Thead {
            org.jetbrains.compose.web.dom.Tr {
                org.jetbrains.compose.web.dom.Th {
                    org.jetbrains.compose.web.dom.Text("Status")
                }
                org.jetbrains.compose.web.dom.Th {
                    org.jetbrains.compose.web.dom.Text("What it says about the record")
                }
                org.jetbrains.compose.web.dom.Th {
                    org.jetbrains.compose.web.dom.Text("Retry?")
                }
            }
        }
        org.jetbrains.compose.web.dom.Tbody {
            org.jetbrains.compose.web.dom.Tr {
                org.jetbrains.compose.web.dom.Td {
                    org.jetbrains.compose.web.dom.Code {
                        org.jetbrains.compose.web.dom.Text("200")
                    }
                }
                org.jetbrains.compose.web.dom.Td {
                    org.jetbrains.compose.web.dom.Text("the broker acknowledged it; the body names partition and offset")
                }
                org.jetbrains.compose.web.dom.Td {
                    org.jetbrains.compose.web.dom.Text("—")
                }
            }
            org.jetbrains.compose.web.dom.Tr {
                org.jetbrains.compose.web.dom.Td {
                    org.jetbrains.compose.web.dom.Code {
                        org.jetbrains.compose.web.dom.Text("429")
                    }
                }
                org.jetbrains.compose.web.dom.Td {
                    org.jetbrains.compose.web.dom.Text("it was provably never queued; ")
                    org.jetbrains.compose.web.dom.Code {
                        org.jetbrains.compose.web.dom.Text("Retry-After")
                    }
                    org.jetbrains.compose.web.dom.Text(" says when")
                }
                org.jetbrains.compose.web.dom.Td {
                    org.jetbrains.compose.web.dom.Text("safe")
                }
            }
            org.jetbrains.compose.web.dom.Tr {
                org.jetbrains.compose.web.dom.Td {
                    org.jetbrains.compose.web.dom.Code {
                        org.jetbrains.compose.web.dom.Text("502")
                    }
                }
                org.jetbrains.compose.web.dom.Td {
                    org.jetbrains.compose.web.dom.Text("the producer refused it before queueing")
                }
                org.jetbrains.compose.web.dom.Td {
                    org.jetbrains.compose.web.dom.Text("safe, though waiting will not help")
                }
            }
            org.jetbrains.compose.web.dom.Tr {
                org.jetbrains.compose.web.dom.Td {
                    org.jetbrains.compose.web.dom.Code {
                        org.jetbrains.compose.web.dom.Text("503")
                    }
                }
                org.jetbrains.compose.web.dom.Td {
                    org.jetbrains.compose.web.dom.Text("the service is draining and did not take it")
                }
                org.jetbrains.compose.web.dom.Td {
                    org.jetbrains.compose.web.dom.Text("safe")
                }
            }
            org.jetbrains.compose.web.dom.Tr {
                org.jetbrains.compose.web.dom.Td {
                    org.jetbrains.compose.web.dom.Code {
                        org.jetbrains.compose.web.dom.Text("504")
                    }
                }
                org.jetbrains.compose.web.dom.Td {
                    org.jetbrains.compose.web.dom.Text("queued, then the deadline passed or delivery failed: ")
                    org.jetbrains.compose.web.dom.B {
                        org.jetbrains.compose.web.dom.Text("it may or may not be written")
                    }
                }
                org.jetbrains.compose.web.dom.Td {
                    org.jetbrains.compose.web.dom.Text("may write it twice")
                }
            }
            org.jetbrains.compose.web.dom.Tr {
                org.jetbrains.compose.web.dom.Td {
                    org.jetbrains.compose.web.dom.Code {
                        org.jetbrains.compose.web.dom.Text("404")
                    }
                    org.jetbrains.compose.web.dom.Text(", ")
                    org.jetbrains.compose.web.dom.Code {
                        org.jetbrains.compose.web.dom.Text("413")
                    }
                }
                org.jetbrains.compose.web.dom.Td {
                    org.jetbrains.compose.web.dom.Text("topic not on the allowlist, or body over the size limit; the producer was never called")
                }
                org.jetbrains.compose.web.dom.Td {
                    org.jetbrains.compose.web.dom.Text("no")
                }
            }
        }
    }
    org.jetbrains.compose.web.dom.P {
        org.jetbrains.compose.web.dom.Code {
            org.jetbrains.compose.web.dom.Text("200")
        }
        org.jetbrains.compose.web.dom.Text(", ")
        org.jetbrains.compose.web.dom.Code {
            org.jetbrains.compose.web.dom.Text("429")
        }
        org.jetbrains.compose.web.dom.Text(" and ")
        org.jetbrains.compose.web.dom.Code {
            org.jetbrains.compose.web.dom.Text("504")
        }
        org.jetbrains.compose.web.dom.Text(" are each checked by reading the topic with somebody else's client, on both builds. So is the shutdown: clients publish under load while the service gets ")
        org.jetbrains.compose.web.dom.Code {
            org.jetbrains.compose.web.dom.Text("SIGTERM")
        }
        org.jetbrains.compose.web.dom.Text(", and every client's ledger is compared with what the topic holds afterwards — zero disagreements in twenty rounds per build.")
    }
    org.jetbrains.compose.web.dom.P {
        org.jetbrains.compose.web.dom.Text("What it does not do is on purpose: authentication (the proxy in front does it, and ")
        org.jetbrains.compose.web.dom.Code {
            org.jetbrains.compose.web.dom.Text("MOSTIK_TOPICS")
        }
        org.jetbrains.compose.web.dom.Text(" is the allowlist), idempotency keys, batches, tombstones, and reading from Kafka.")
    }
    org.jetbrains.compose.web.dom.H2(attrs = { id("install") }) {
        org.jetbrains.compose.web.dom.Text("Install")
    }
    org.jetbrains.compose.web.dom.P {
        org.jetbrains.compose.web.dom.Text("It ships twice from one source — a Kotlin/Native ")
        org.jetbrains.compose.web.dom.Code {
            org.jetbrains.compose.web.dom.Text("linuxX64")
        }
        org.jetbrains.compose.web.dom.Text(" binary and a JVM distribution — and both are built from the repository. ")
        org.jetbrains.compose.web.dom.B {
            org.jetbrains.compose.web.dom.Text("There is no published image yet")
        }
        org.jetbrains.compose.web.dom.Text("; the ")
        org.jetbrains.compose.web.dom.Code {
            org.jetbrains.compose.web.dom.Text("Dockerfile")
        }
        org.jetbrains.compose.web.dom.Text(" puts the native binary on ")
        org.jetbrains.compose.web.dom.Code {
            org.jetbrains.compose.web.dom.Text("gcr.io/distroless/cc-debian13")
        }
        org.jetbrains.compose.web.dom.Text(".")
    }
    org.jetbrains.compose.web.dom.Pre { org.jetbrains.compose.web.dom.Code(attrs = { classes("language-bash") }) { org.jetbrains.compose.web.dom.Text("""ci/broker/broker.sh up                                     # a broker on 127.0.0.1:19092

./gradlew :server:linkReleaseExecutableLinuxX64            # native; builds on Linux x86-64
MOSTIK_BOOTSTRAP_SERVERS=127.0.0.1:19092 MOSTIK_TOPICS=orders,payments \
  server/build/bin/linuxX64/releaseExecutable/mostik.kexe

./gradlew :distribution:installDist                       # JVM, any host with a JDK
MOSTIK_BOOTSTRAP_SERVERS=127.0.0.1:19092 MOSTIK_TOPICS=orders,payments \
  distribution/build/install/distribution/bin/distribution
""") } }
    org.jetbrains.compose.web.dom.P {
        org.jetbrains.compose.web.dom.Text("Everything comes from the environment. A ")
        org.jetbrains.compose.web.dom.Code {
            org.jetbrains.compose.web.dom.Text("MOSTIK_")
        }
        org.jetbrains.compose.web.dom.Text(" variable not in the table stops the start, and so does a budget that cannot hold — a drain shorter than the publish deadline plus a second, or a queue wait not shorter than the deadline — with both values named. ")
        org.jetbrains.compose.web.dom.Code {
            org.jetbrains.compose.web.dom.Text("KAFKA_*")
        }
        org.jetbrains.compose.web.dom.Text(" keys pass through to the producer; ")
        org.jetbrains.compose.web.dom.Code {
            org.jetbrains.compose.web.dom.Text("KAFKA_BOOTSTRAP_SERVERS")
        }
        org.jetbrains.compose.web.dom.Text(" and ")
        org.jetbrains.compose.web.dom.Code {
            org.jetbrains.compose.web.dom.Text("KAFKA_MAX_BLOCK_MS")
        }
        org.jetbrains.compose.web.dom.Text(" are refused, because mostik sets those itself and one value with two sources is a disagreement waiting to happen.")
    }
    org.jetbrains.compose.web.dom.H2(attrs = { id("how-the-deadline-is-split") }) {
        org.jetbrains.compose.web.dom.Text("How the deadline is split")
    }
    org.jetbrains.compose.web.dom.P {
        org.jetbrains.compose.web.dom.Text("Each publish is two calls into ")
        com.varabyte.kobweb.silk.components.navigation.Link("/kafkakn") {
            org.jetbrains.compose.web.dom.Text("kafkakn")
        }
        org.jetbrains.compose.web.dom.Text(". First ")
        org.jetbrains.compose.web.dom.Code {
            org.jetbrains.compose.web.dom.Text("enqueue")
        }
        org.jetbrains.compose.web.dom.Text(", bounded by ")
        org.jetbrains.compose.web.dom.Code {
            org.jetbrains.compose.web.dom.Text("max.block.ms")
        }
        org.jetbrains.compose.web.dom.Text(" (")
        org.jetbrains.compose.web.dom.Code {
            org.jetbrains.compose.web.dom.Text("MOSTIK_QUEUE_WAIT_MS")
        }
        org.jetbrains.compose.web.dom.Text(", 1 s): a failure there is provably not written, so it is ")
        org.jetbrains.compose.web.dom.Code {
            org.jetbrains.compose.web.dom.Text("429")
        }
        org.jetbrains.compose.web.dom.Text(". Then ")
        org.jetbrains.compose.web.dom.Code {
            org.jetbrains.compose.web.dom.Text("await")
        }
        org.jetbrains.compose.web.dom.Text(", under what is left of ")
        org.jetbrains.compose.web.dom.Code {
            org.jetbrains.compose.web.dom.Text("MOSTIK_PUBLISH_DEADLINE_MS")
        }
        org.jetbrains.compose.web.dom.Text(" (5 s): a failure there is ")
        org.jetbrains.compose.web.dom.Code {
            org.jetbrains.compose.web.dom.Text("504")
        }
        org.jetbrains.compose.web.dom.Text(". The first half of that API exists because mostik asked for it. ")
        com.varabyte.kobweb.silk.components.navigation.Link("/kore") {
            org.jetbrains.compose.web.dom.Text("kore")
        }
        org.jetbrains.compose.web.dom.Text(" runs the lifecycle — the configuration schema, the three probes, and the ordered stop: announce for 5 s with readiness at ")
        org.jetbrains.compose.web.dom.Code {
            org.jetbrains.compose.web.dom.Text("503")
        }
        org.jetbrains.compose.web.dom.Text(", drain the requests in flight, then close the producer, cut after 3 s if the broker is gone.")
    }
    org.jetbrains.compose.web.dom.H2(attrs = { id("limitations") }) {
        org.jetbrains.compose.web.dom.Text("Limitations")
    }
    org.jetbrains.compose.web.dom.Ul {
        org.jetbrains.compose.web.dom.Li {
            org.jetbrains.compose.web.dom.B {
                org.jetbrains.compose.web.dom.Text("No published image.")
            }
            org.jetbrains.compose.web.dom.Text(" Build it, or run the binary or the JVM distribution from the repository.")
        }
        org.jetbrains.compose.web.dom.Li {
            org.jetbrains.compose.web.dom.Text("On the native build, Ktor's CIO engine occasionally closes a connection without sending the answer the route already wrote — between once in 60 000 and once in 375 000 requests under 64 clients. The client then has no answer and must treat it like a ")
            org.jetbrains.compose.web.dom.Code {
                org.jetbrains.compose.web.dom.Text("504")
            }
            org.jetbrains.compose.web.dom.Text(". The JVM build has shown none; it reproduces with Ktor alone, in a separate repository.")
        }
        org.jetbrains.compose.web.dom.Li {
            org.jetbrains.compose.web.dom.Text("The two builds disagree in one measured case: with the broker stopped, not paused, the JVM build answers ")
            org.jetbrains.compose.web.dom.Code {
                org.jetbrains.compose.web.dom.Text("429")
            }
            org.jetbrains.compose.web.dom.Text(" and the native one ")
            org.jetbrains.compose.web.dom.Code {
                org.jetbrains.compose.web.dom.Text("504")
            }
            org.jetbrains.compose.web.dom.Text(". It is written down rather than papered over.")
        }
        org.jetbrains.compose.web.dom.Li {
            org.jetbrains.compose.web.dom.Text("kafkakn, kore, sborka and razves come from reposilite, not Maven Central; ")
            org.jetbrains.compose.web.dom.Code {
                org.jetbrains.compose.web.dom.Text("settings.gradle.kts")
            }
            org.jetbrains.compose.web.dom.Text(" declares the repository, filtered to their group.")
        }
    }
}
